import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  Grid,
  Alert,
  CircularProgress,
  Paper,
} from "@mui/material";
import {
  LabeledField,
} from "@/__core/components/LabeledField";
import { EmployeeDisplay, EmployeeAvatarGroup } from '@/__core/components';
import { AddButtonSelect } from "@/__core/hocs";
import GroupCreationDialog from "./GroupCreationDialog";
import TaskFilters from "./TaskFilters";
import NewTaskModal from "./NewTaskModal";
import DataTableModule from '@/__core/layout/module/DataTableModule';
import { Add as AddIcon, Assignment as AssignmentIcon } from "@mui/icons-material";
import { format } from "date-fns";
import { vi } from "date-fns/locale";
import {
  Task,
  TaskStatus,
  TaskPriority,
  User,
  Group,
  AssignTaskRequest,
  UpdateTaskRequest,
} from "../models/TaskModel";

interface ListTasksOrderProps {
  tasks: Task[];
  loading: boolean;
  taskStatuses: TaskStatus[];
  taskPriorities: TaskPriority[];
  users: User[];
  groups: Group[];
  contextCompanyId?: string;
  orderId?: string;
  getUserDisplayName: (user: User) => string;
  onCreateTask: () => void;
  onAssignTask: (taskId: string, assignData: AssignTaskRequest) => Promise<void>;
  onEditTask?: (taskId: string, updateData: UpdateTaskRequest) => Promise<Task | void>;
  onDeleteTask?: (taskId: string) => void;
  onStatusChange?: (taskId: string, statusId: string) => void;
  onFiltersChange?: (filters: { assigned_by?: string; assigned_to_group?: string }) => void;
}

type DisplayRow = {
  rowId: string;
  task: Task;
  relatedCategory: string;
  relatedObjectDisplay: string;
};

const ListTasksOrder: React.FC<ListTasksOrderProps> = ({
  tasks,
  loading,
  taskStatuses,
  taskPriorities,
  users,
  groups,
  contextCompanyId,
  orderId,
  getUserDisplayName,
  onCreateTask,
  onAssignTask,
  onEditTask,
  onDeleteTask,
  onStatusChange,
  onFiltersChange,
}) => {
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [assignForm, setAssignForm] = useState({
    assigned_to_user_id: "",
    assigned_to_group_id: "",
  });

  // Edit task modal state
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [selectedPriorities, setSelectedPriorities] = useState<string[]>([]);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<{start: string, end: string}>({start: '', end: ''});
  const previousAssignedByRef = useRef<string | undefined>(undefined);
  const previousAssignedToGroupRef = useRef<string | undefined>(undefined);

  // Filter handlers
  const handleStatusChange = (statusId: string) => {
    setSelectedStatuses(prev => 
      prev.includes(statusId) 
        ? prev.filter(id => id !== statusId)
        : [...prev, statusId]
    );
  };

  const handlePriorityChange = (priorityId: string) => {
    setSelectedPriorities(prev => 
      prev.includes(priorityId) 
        ? prev.filter(id => id !== priorityId)
        : [...prev, priorityId]
    );
  };

  const handleAssigneeChange = (assigneeId: string) => {
    setSelectedAssignees(prev => 
      prev.includes(assigneeId) 
        ? prev.filter(id => id !== assigneeId)
        : [...prev, assigneeId]
    );
  };

  const handleGroupFilterChange = (groupId: string) => {
    setSelectedGroups(prev =>
      prev.includes(groupId)
        ? prev.filter(id => id !== groupId)
        : [...prev, groupId]
    );
  };

  useEffect(() => {
    if (!onFiltersChange) return;

    const selectedEmployeeId = selectedAssignees.find(
      (id) => id !== 'unassigned'
    );
    const selectedGroupId = selectedGroups[0];

    // Avoid repeated calls when filters have not actually changed.
    if (
      previousAssignedByRef.current === selectedEmployeeId &&
      previousAssignedToGroupRef.current === selectedGroupId
    ) {
      return;
    }

    previousAssignedByRef.current = selectedEmployeeId;
    previousAssignedToGroupRef.current = selectedGroupId;

    onFiltersChange({
      assigned_by: selectedEmployeeId,
      assigned_to_group: selectedGroupId,
    });
  }, [selectedAssignees, selectedGroups, onFiltersChange]);

  const handleDateRangeChange = (range: { start: string; end: string }) => {
    setDateRange(range);
  };

  // Handle row click to edit task
  const handleRowClick = (task: Task) => {
    setEditingTask(task);
    setEditModalOpen(true);
  };

  // Filter tasks based on current filters
  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      const taskAny = task as any;

      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch = 
          task.title.toLowerCase().includes(searchLower) ||
          task.content.toLowerCase().includes(searchLower);
        if (!matchesSearch) return false;
      }

      // Status filter
      if (selectedStatuses.length > 0) {
        const statusId = typeof task.status === 'string' ? task.status : task.status?.id;
        if (!statusId || !selectedStatuses.includes(statusId)) {
          return false;
        }
      }

      // Priority filter
      if (selectedPriorities.length > 0) {
        const priorityId = typeof task.priority === 'string' ? task.priority : task.priority?.id;
        if (!priorityId || !selectedPriorities.includes(priorityId)) {
          return false;
        }
      }

      // Assignee filter
      if (selectedAssignees.length > 0) {
        const assignedById =
          (typeof taskAny.assigned_by === 'string' ? taskAny.assigned_by : taskAny.assigned_by?.id) ||
          (typeof task.created_by === 'string' ? task.created_by : task.created_by?.id);
        const isAssignedToUser = assignedById && selectedAssignees.includes(assignedById);
        const isUnassigned = !assignedById && selectedAssignees.includes('unassigned');
        
        if (!isAssignedToUser && !isUnassigned) {
          return false;
        }
      }

      // Group filter
      if (selectedGroups.length > 0) {
        const groupId = typeof task.assigned_to_group === 'string' ? task.assigned_to_group : task.assigned_to_group?.id;
        if (!groupId || !selectedGroups.includes(groupId)) {
          return false;
        }
      }

      // Date range filter
      if (dateRange.start) {
        const taskStartDate = new Date(task.start_date);
        const filterStartDate = new Date(dateRange.start);
        if (taskStartDate < filterStartDate) return false;
      }

      if (dateRange.end) {
        const taskDueDate = new Date(task.due_date);
        const filterEndDate = new Date(dateRange.end);
        if (taskDueDate > filterEndDate) return false;
      }

      return true;
    });
  }, [tasks, searchTerm, selectedStatuses, selectedPriorities, selectedAssignees, selectedGroups, dateRange]);

  const handleAssignTask = async () => {
    if (!selectedTask) return;
    
    try {
      // Ensure only one assignment is sent (user takes priority over group)
      // Send null to explicitly clear the other assignment
      const assignData: AssignTaskRequest = {
        assigned_to_user_id: (assignForm.assigned_to_user_id || null) as string | null,
        assigned_to_group_id: (assignForm.assigned_to_user_id ? null : (assignForm.assigned_to_group_id || null)) as string | null,
      };
      
      await onAssignTask(selectedTask.id, assignData);
      
      setAssignDialogOpen(false);
      setSelectedTask(null);
      setAssignForm({ assigned_to_user_id: "", assigned_to_group_id: "" });
    } catch (err: any) {
      setError(err.message || "Không thể gán task");
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';
    return format(date, "dd/MM/yyyy", { locale: vi });
  };

  const getStatusLabel = (task: Task): string => {
    if (task.status_name) {
      return task.status_name;
    }
    if (typeof task.status === 'object' && task.status?.name) {
      return task.status.name;
    }
    return 'Không xác định';
  };

  const getStatusColor = (task: Task) => {
    return task.status_color || (typeof task.status === 'object' ? task.status?.color : '') || '#6c757d';
  };

  const getPriorityLabel = (task: Task) => {
    return task.priority_name || (typeof task.priority === 'object' ? task.priority?.name : '') || 'Chưa xác định';
  };

  const getPriorityColor = (task: Task) => {
    return task.priority_color || (typeof task.priority === 'object' ? task.priority?.color : '') || '#6c757d';
  };

  

  const displayRows = useMemo<DisplayRow[]>(() => {
    return filteredTasks.flatMap((task) => {
      const relatedModelObjects = Array.isArray((task as any)?.related_model_objects)
        ? ((task as any).related_model_objects as any[])
        : [];

      if (!relatedModelObjects.length) {
        return [{
          rowId: task.id,
          task,
          relatedCategory: String((task as any)?.related_object?.model?.verbose_name || '-').trim() || '-',
          relatedObjectDisplay: String((task as any)?.related_object?.object?.display || '-').trim() || '-',
        }];
      }

      return relatedModelObjects.map((relatedItem: any, index: number) => ({
        rowId: `${task.id}::${index}`,
        task,
        relatedCategory: String(relatedItem?.model?.verbose_name || relatedItem?.model?.name || '-').trim() || '-',
        relatedObjectDisplay: String(relatedItem?.object?.display || relatedItem?.display || relatedItem?.object?.id || '-').trim() || '-',
      }));
    });
  }, [filteredTasks]);

  const filteredRowIds = useMemo(() => displayRows.map((row) => row.rowId), [displayRows]);

  useEffect(() => {
    const filteredIdSet = new Set(filteredRowIds);
    setSelectedTaskIds((prev) => prev.filter((id) => filteredIdSet.has(id)));
  }, [filteredRowIds]);

  const handleRowSelect = (taskId: string) => {
    setSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId]
    );
  };

  const handleSelectAll = () => {
    setSelectedTaskIds((prev) => {
      const allSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !filteredRowIds.includes(id));
      }

      const merged = new Set([...prev, ...filteredRowIds]);
      return Array.from(merged);
    });
  };

  const areAllRowsSelected = filteredRowIds.length > 0 && filteredRowIds.every((id) => selectedTaskIds.includes(id));
  const areSomeRowsSelected = filteredRowIds.some((id) => selectedTaskIds.includes(id)) && !areAllRowsSelected;

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Đang tải danh sách task...</Typography>
      </Box>
    );
  }

  // Create columns for DataTableModule
  const columns = [
    {
      id: 'title',
      label: 'Tên nhiệm vụ',
      width: '200px',
      render: (_value: any, item: DisplayRow) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {item.task.title || '-'}
        </Typography>
      ),
    },
    {
      id: 'related_category',
      label: 'Danh mục',
      width: '140px',
      render: (_value: any, item: DisplayRow) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {item.relatedCategory}
        </Typography>
      ),
    },
    {
      id: 'related_object_display',
      label: 'Đối tượng',
      width: '180px',
      render: (_value: any, item: DisplayRow) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.75rem',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 180
          }}
          title={item.relatedObjectDisplay}
        >
          {item.relatedObjectDisplay}
        </Typography>
      ),
    },
    {
      id: 'status_name',
      label: 'Trạng thái',
      width: '120px',
      render: (_value: any, item: DisplayRow) => (
        <Typography
          variant="body2"
          sx={{
            fontSize: '0.75rem',
            fontWeight: 500,
            color: getStatusColor(item.task),
          }}
        >
          {getStatusLabel(item.task)}
        </Typography>
      ),
    },
    {
      id: 'priority_name',
      label: 'Độ ưu tiên',
      width: '120px',
      render: (_value: any, item: DisplayRow) => (
        <Typography
          variant="body2"
          sx={{
            color: getPriorityColor(item.task),
            fontWeight: 500,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {getPriorityLabel(item.task)}
        </Typography>
      ),
    },
    {
      id: 'assigned_to_user_name',
      label: 'Người thực hiện',
      width: '200px',
      render: (_value: any, item: DisplayRow) => {
        const assignedEmployees = Array.isArray((item.task as any)?.assigned_to_employees)
          ? (item.task as any).assigned_to_employees as any[]
          : [];
        if (assignedEmployees.length === 0) {
          return <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>-</Typography>;
        }
        if (assignedEmployees.length >= 3) {
          return (
            <EmployeeAvatarGroup
              employees={assignedEmployees.map((emp: any) => ({
                id: emp.id,
                name: emp.name,
                nickname: emp.nickname,
                avatar: emp.user?.avatar || null,
              }))}
            />
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
            {assignedEmployees.map((emp: any) => (
              <EmployeeDisplay
                key={emp.id}
                name={emp.name}
                nickname={emp.nickname}
                avatar={emp.user?.avatar || null}
                image1={undefined}
                employeeId={emp.id}
                userId={emp.user?.id}
              />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'following_employees',
      label: 'Người quan sát',
      width: '200px',
      render: (_value: any, item: DisplayRow) => {
        const followingEmployees = Array.isArray((item.task as any)?.following_employees)
          ? (item.task as any).following_employees as any[]
          : [];
        if (followingEmployees.length === 0) {
          return <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>-</Typography>;
        }
        if (followingEmployees.length >= 3) {
          return (
            <EmployeeAvatarGroup
              employees={followingEmployees.map((emp: any) => ({
                id: emp.id,
                name: emp.name,
                nickname: emp.nickname,
                avatar: emp.user?.avatar || null,
              }))}
            />
          );
        }
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '2px 8px' }}>
            {followingEmployees.map((emp: any) => (
              <EmployeeDisplay
                key={emp.id}
                name={emp.name}
                nickname={emp.nickname}
                avatar={emp.user?.avatar || null}
                image1={undefined}
                employeeId={emp.id}
                userId={emp.user?.id}
              />
            ))}
          </Box>
        );
      },
    },
    {
      id: 'start_date',
      label: 'Ngày bắt đầu',
      width: '120px',
      render: (_value: any, item: DisplayRow) => (
        <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
          {formatDate(item.task.start_date)}
        </Typography>
      ),
    },
    {
      id: 'due_date',
      label: 'Ngày hết hạn',
      width: '120px',
      render: (_value: any, item: DisplayRow) => (
        <Typography 
          variant="body2" 
          color={item.task.is_overdue ? "error" : "text.primary"}
          sx={{ 
            fontWeight: item.task.is_overdue ? 600 : 400,
            fontSize: '0.75rem'
          }}
        >
          {formatDate(item.task.due_date)}
        </Typography>
      ),
    },

  ];

  return (
    <Box sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: "flex", 
          justifyContent: "space-between", 
          alignItems: "center"
        }}>
          <Typography variant="h6">Danh sách nhiệm vụ</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            onClick={onCreateTask}
          >
            Tạo nhiệm vụ
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2, mx: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Main Content */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        {/* Left Sidebar - Filters */}
        <TaskFilters
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedStatuses={selectedStatuses}
          onStatusChange={handleStatusChange}
          selectedPriorities={selectedPriorities}
          onPriorityChange={handlePriorityChange}
          selectedAssignees={selectedAssignees}
          onAssigneeChange={handleAssigneeChange}
          selectedGroups={selectedGroups}
          onGroupChange={handleGroupFilterChange}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          taskStatuses={taskStatuses}
          taskPriorities={taskPriorities}
          users={users}
          groups={groups}
          companyId={contextCompanyId}
          orderId={orderId}
          loading={loading}
        />

        {/* Right Content - Tasks Table */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {/* Tasks List */}
          {(!tasks || tasks.length === 0) ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              width: '100%',
              textAlign: "center", 
              py: 4 
            }}>
              <AssignmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Chưa có task nào
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tạo task đầu tiên để bắt đầu quản lý công việc
              </Typography>
            </Box>
          ) : filteredTasks.length === 0 ? (
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              justifyContent: 'center', 
              width: '100%',
              textAlign: "center", 
              py: 4 
            }}>
              <AssignmentIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Không tìm thấy task nào
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Thử thay đổi bộ lọc để xem kết quả khác
              </Typography>
            </Box>
          ) : (
            <DataTableModule
              data={displayRows}
              columns={columns}
              getRowId={(item: DisplayRow) => item.rowId}
              onRowSelect={handleRowSelect}
              onSelectAll={handleSelectAll}
              onRowClick={(row: DisplayRow) => handleRowClick(row.task)}
              isRowSelected={(row: DisplayRow) => selectedTaskIds.includes(row.rowId)}
              areAllRowsSelected={areAllRowsSelected}
              areSomeRowsSelected={areSomeRowsSelected}
              showCheckbox={true}
              leftFilterWidth={280}
            />
          )}
        </Box>
      </Box>

      {/* Assign Task Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gán task: {selectedTask?.title}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{xs: 12}}>
              <LabeledField label="Gán cho user" labelWidth={120}>
                <FormControl fullWidth size="small">
                  <Select
                    value={assignForm.assigned_to_user_id}
                    onChange={(e) => setAssignForm({ 
                      ...assignForm, 
                      assigned_to_user_id: e.target.value,
                      assigned_to_group_id: "" // Clear group if user is selected
                    })}
                    native
                    sx={{
                      fontSize: '14px',
                      paddingLeft: '12px',
                      '& select': {
                        paddingLeft: '12px',
                      }
                    }}
                  >
                    <option value="">Không gán</option>
                    {(users || []).map((user: User) => (
                      <option key={user.id} value={user.id}>
                        {getUserDisplayName(user)}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </LabeledField>
            </Grid>
            <Grid size={{xs: 12}}>
              <LabeledField label="Gán cho group" labelWidth={120}>
                <AddButtonSelect
                  key={`assign-group-select-${groups?.length || 0}`} // Force re-render when groups change
                  options={(groups || []).map((group: Group) => ({ ...group, name: group.display_name }))}
                  value={assignForm.assigned_to_group_id}
                  onChange={(event: any) => setAssignForm({ 
                    ...assignForm, 
                    assigned_to_group_id: event.target.value,
                    assigned_to_user_id: "" // Clear user if group is selected
                  })}
                  placeholder="Không gán"
                  size="small"
                  disabled={false}
                  CustomDialog={GroupCreationDialog}
                  customDialogProps={{ companyId: contextCompanyId }}
                  onAddNewCustom={async (groupData: any) => {
                    // groupData is a Redux action object, extract the actual group data
                    const actualGroup = groupData.payload || groupData;
                    const groupId = actualGroup.id;
                    
                    // Update the assign form immediately when group is created
                    setAssignForm(prev => ({ 
                      ...prev, 
                      assigned_to_group_id: groupId,
                      assigned_to_user_id: "" // Clear user if group is selected
                    }));

                    // Return the group data for AddButtonSelect to use
                    return {
                      id: groupId,
                      name: actualGroup.display_name,
                    };
                  }}
                />
              </LabeledField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleAssignTask} variant="contained">
            Gán task
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Task Modal */}
      <NewTaskModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingTask(null);
        }}
        onSubmit={async (taskData) => {
          if (editingTask && onEditTask) {
            // Extract assignment fields to handle separately
            const { assigned_to_user_id, assigned_to_group_id, ...restTaskData } = taskData;
            
            // Create update request with task data, filter out empty values
            const updateData = {
              ...restTaskData,
              // Ensure only one assignment is sent (user takes priority over group)
              // Send null to explicitly clear the other assignment
              assigned_to_user_id: assigned_to_user_id || null,
              assigned_to_group_id: assigned_to_user_id ? null : (assigned_to_group_id || null),
            };
            
            // Filter out empty string values and undefined, but keep null for assignment fields
            let filteredUpdateData = Object.fromEntries(
              Object.entries(updateData).filter(([key, value]) => {
                // Keep null values for assignment fields to clear them
                if (key === 'assigned_to_user_id' || key === 'assigned_to_group_id') {
                  return true;
                }
                // Filter out empty values for other fields
                return value !== undefined && value !== "" && value !== null;
              })
            );
            
            console.log("Filtered update data:", filteredUpdateData);
            return onEditTask(editingTask.id, filteredUpdateData);
          }
          return undefined;
        }}
        taskStatuses={taskStatuses}
        taskPriorities={taskPriorities}
        users={users}
        companyId={contextCompanyId}
        orderId={orderId}
        getUserDisplayName={getUserDisplayName}
        editingTask={editingTask}
        isEditMode={true}
      />
    </Box>
  );
};

export default ListTasksOrder;
